import { supabase } from '../../supabaseClient';
import { SERVICE_STATUSES } from '../../constants';

// ========================
// REVIEWS SERVICES
// ========================

/**
 * Crea una nueva review para un servicio completado
 */
export async function createReview(reviewData) {
  const { data, error } = await supabase
    .from('reviews')
    .insert(reviewData)
    .select()
    .single();
  if (error) throw error;
  return data;
}

/**
 * Obtiene reviews de un usuario (como reviewer)
 */
export async function getUserReviews(userId, reviewType = null) {
  let query = supabase
    .from('reviews')
    .select(`
      *,
      services:service_id (
        type,
        price,
        distance,
        created_at,
        pickup,
        dropoff
      ),
      reviewer:reviewer_id (
        name
      ),
      reviewed:reviewed_id (
        name
      )
    `)
    .eq('reviewer_id', userId)
    .order('created_at', { ascending: false });

  if (reviewType) {
    query = query.eq('review_type', reviewType);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Obtiene reviews recibidas por un usuario
 */
export async function getReceivedReviews(userId, reviewType = null) {
  let query = supabase
    .from('reviews')
    .select(`
      *,
      services:service_id (
        type,
        price,
        distance,
        created_at
      ),
      reviewer:reviewer_id (
        name
      )
    `)
    .eq('reviewed_id', userId)
    .order('created_at', { ascending: false });

  if (reviewType) {
    query = query.eq('review_type', reviewType);
  }

  const { data, error } = await query;
  if (error) throw error;
  return data || [];
}

/**
 * Obtiene el rating promedio y estadísticas de un usuario (RLS-safe)
 */
export async function getUserRating(userId) {
  if (!userId) return null;

  const { data, error } = await supabase
    .rpc('get_user_rating_safe', { user_uuid: userId });
  
  if (error) throw error;
  return data?.[0] || {
    average_rating: 0,
    total_reviews: 0,
    positive_reviews: 0,
    neutral_reviews: 0,
    negative_reviews: 0
  };
}

/**
 * Obtiene rating con Wilson Score (método robusto) (RLS-safe)
 */
export async function getUserRatingWilson(userId) {
  if (!userId) return null;
  try {
    const { data, error } = await supabase
      .rpc('get_user_rating_wilson_safe', { user_uuid: userId });
    
    if (error) {
      console.warn('RPC get_user_rating_wilson_safe error:', error.message);
      return {
        wilson_score_rating: 0, average_rating: 0, total_reviews: 0,
        positive_reviews: 0, neutral_reviews: 0, negative_reviews: 0, confidence_level: 0.7
      };
    }
    return data?.[0] || {
      wilson_score_rating: 0, average_rating: 0, total_reviews: 0,
      positive_reviews: 0, neutral_reviews: 0, negative_reviews: 0, confidence_level: 0.7
    };
  } catch (err) {
    console.warn('RPC get_user_rating_wilson_safe failed:', err);
    return {
      wilson_score_rating: 0, average_rating: 0, total_reviews: 0,
      positive_reviews: 0, neutral_reviews: 0, negative_reviews: 0, confidence_level: 0.7
    };
  }
}

/**
 * Calcula el límite inferior del intervalo de confianza de Wilson Score (0 a 5 estrellas).
 * Con 1 sola valoración positiva (1 de 1) y 95% de confianza (z=1.96), devuelve 1.03 (nunca 5.0).
 * Con 0 valoraciones devuelve 0.
 */
export function calculateWilsonScoreClient(positiveReviews, totalReviews, confidenceLevel = 0.95) {
  const n = Number(totalReviews || 0);
  const pos = Number(positiveReviews || 0);
  if (n <= 0 || pos < 0 || pos > n) return 0;

  const z = confidenceLevel === 0.99 ? 2.576 : confidenceLevel === 0.90 ? 1.645 : 1.96;
  const phat = pos / n;
  const z2 = z * z;
  const numerator = phat + z2 / (2 * n) - z * Math.sqrt((phat * (1 - phat) + z2 / (4 * n)) / n);
  const denominator = 1 + z2 / n;
  const wilson = Math.max(0, Math.min(1, numerator / denominator));
  return Math.round(wilson * 5 * 100) / 100;
}

/**
 * Obtiene top conductores usando Wilson Score (RLS-safe, admin only)
 */
export async function getTopDriversWilson(limit = 10) {
  try {
    const { data, error } = await supabase
      .rpc('get_top_drivers_wilson_safe', { limit_count: limit });
    
    if (!error && data && data.length > 0) {
      return data;
    }
  } catch (_) {}

  // Fast direct fallback from indexed drivers table
  try {
    const { data } = await supabase
      .from('drivers')
      .select('driver_uid, rating, total_trips, name, vehicle_type')
      .eq('status', 'active')
      .order('rating', { ascending: false })
      .limit(limit);
    
    return (data || []).map(d => ({
      ...d,
      wilson_score_rating: Number(d.rating || 0),
      total_reviews: 0
    }));
  } catch (_) {
    return [];
  }
}

/**
 * Verifica si un usuario ya hizo review para un servicio
 */
export async function hasUserReviewedService(userId, serviceId, reviewType) {
  const { data, error } = await supabase
    .from('reviews')
    .select('review_id')
    .eq('reviewer_id', userId)
    .eq('service_id', serviceId)
    .eq('review_type', reviewType)
    .maybeSingle();

  if (error) throw error;
  return !!data;
}

/**
 * Obtiene reviews pendientes para un usuario (servicios completados sin review)
 */
export async function getPendingReviews(userId, userType) {
  const reviewType = userType === 'customer' ? 'customer_to_driver' : 'driver_to_customer';
  const userField = userType === 'customer' ? 'user_uid' : 'driver_uid';

  const { data, error } = await supabase
    .from('services')
    .select(`
      *,
      reviews:reviews (
        review_id,
        review_type
      )
    `)
    .eq(userField, userId)
    .eq('status', SERVICE_STATUSES.COMPLETED)
    .order('created_at', { ascending: false });

  if (error) throw error;

  // Filtrar servicios que no tienen review del tipo correspondiente
  const pendingServices = (data || []).filter(service => {
    return !service.reviews?.some(review => review.review_type === reviewType);
  });

  return pendingServices;
}

/**
 * Actualiza una review existente
 */
export async function updateReview(reviewId, updateData) {
  const { data, error } = await supabase
    .from('reviews')
    .update(updateData)
    .eq('review_id', reviewId)
    .select()
    .single();

  if (error) throw error;
  return data;
}

/**
 * Elimina una review
 */
export async function deleteReview(reviewId) {
  const { error } = await supabase
    .from('reviews')
    .delete()
    .eq('review_id', reviewId);

  if (error) throw error;
}
